/**
 * Data Quality Utilities
 * Calculates completeness scores and provides data quality metrics
 */

/**
 * Calculates the completeness percentage for an entity
 * @param entity - The entity object to check
 * @param fields - Array of field names to check
 * @returns Percentage (0-100) of fields that have values
 */
export function calculateCompleteness<T extends object>(entity: T, fields: string[]): number {
  if (!entity || fields.length === 0) {
    return 0
  }

  const filledFields = fields.filter((field) => {
    const value = (entity as Record<string, unknown>)[field]

    // Check if field has a meaningful value
    if (value === null || value === undefined) {
      return false
    }

    // Empty string check
    if (typeof value === 'string' && value.trim() === '') {
      return false
    }

    // Empty array check
    if (Array.isArray(value) && value.length === 0) {
      return false
    }

    return true
  })

  return Math.round((filledFields.length / fields.length) * 100)
}

/**
 * Gets a color variant based on completeness percentage
 * @param percentage - Completeness percentage (0-100)
 * @returns Variant string for Badge component
 */
export function getCompletenessVariant(
  percentage: number
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (percentage >= 80) {
    return 'secondary' // Green
  } else if (percentage >= 50) {
    return 'outline' // Yellow/Amber
  } else {
    return 'destructive' // Red
  }
}

/**
 * Gets a descriptive label for completeness score
 * @param percentage - Completeness percentage (0-100)
 * @returns Descriptive string
 */
export function getCompletenessLabel(percentage: number): string {
  if (percentage >= 90) {
    return 'Excellent'
  } else if (percentage >= 80) {
    return 'Good'
  } else if (percentage >= 60) {
    return 'Fair'
  } else if (percentage >= 40) {
    return 'Poor'
  } else {
    return 'Very Poor'
  }
}

/**
 * Gets missing fields from an entity
 * @param entity - The entity object to check
 * @param fields - Array of field names to check
 * @returns Array of field names that are missing or empty
 */
export function getMissingFields(entity: Record<string, unknown>, fields: string[]): string[] {
  return fields.filter((field) => {
    const value = entity[field]

    if (value === null || value === undefined) {
      return true
    }

    if (typeof value === 'string' && value.trim() === '') {
      return true
    }

    if (Array.isArray(value) && value.length === 0) {
      return true
    }

    return false
  })
}

/**
 * Formats field names to human-readable labels
 * @param fieldName - Snake_case or camelCase field name
 * @returns Human-readable label
 */
export function formatFieldName(fieldName: string): string {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Important fields for each entity type
 * These are used to calculate completeness scores
 */
export const IMPORTANT_FIELDS = {
  lead: ['last_name', 'company', 'email', 'phone', 'status', 'lead_source', 'industry'],
  opportunity: [
    'name',
    'account_id',
    'stage_name',
    'amount',
    'close_date',
    'probability',
    'type',
    'lead_source',
  ],
  account: ['name', 'phone', 'website', 'industry', 'type', 'billing_city', 'billing_state'],
  contact: ['first_name', 'last_name', 'email', 'phone', 'account_id', 'title', 'mailing_city'],
  task: ['subject', 'status', 'priority', 'activity_date', 'owner_id'],
  event: ['subject', 'start_datetime', 'end_datetime', 'owner_id'],
  quote: ['name', 'opportunity_id', 'status', 'total_price', 'expiration_date'],
  product: ['name', 'product_code', 'family', 'is_active'],
}

/**
 * Gets important fields for an entity type
 * @param entityType - Type of entity (lead, opportunity, etc.)
 * @returns Array of important field names for that entity type
 */
export function getImportantFields(entityType: keyof typeof IMPORTANT_FIELDS): string[] {
  return IMPORTANT_FIELDS[entityType] || []
}
